import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../i18n/config";
import GetData from "../../services/getData";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import historyTransaction from "../../media/history_transaction.png";
import "./accounts.css";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import { Row, Col, Container } from "react-bootstrap";
import filterIcon from "../../media/filter_icon.png";

function Accounts() {
  const { t, i18n } = useTranslation("finance");
  const locale = getLocale(i18n.language);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState("all");
  const [viewMode, setViewMode] = useState("overview"); // overview, clinic, transactions
  const [filterStatus, setFilterStatus] = useState("all"); // all, paid, unpaid
  const [filterMethod, setFilterMethod] = useState("all"); // all, cash, card, invoice
  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    searchInputRef,
  } = useDebouncedSearch();

  // Вспомогательные функции - объявляем их ПЕРЕД useMemo
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";
    const date = new Date(dateTimeString);
    return date.toLocaleString(locale, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "RUB",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Цветовая схема
  // Ключи — имена статусов с сервера (данные, не подписи UI).
  const colors = {
    primary: "#4361ee",
    secondary: "#3a0ca3",
    success: "#4cc9f0",
    danger: "#f72585",
    warning: "#f8961e",
    info: "#43aa8b",
    paid: "#38b000",
    unpaid: "#ff6b6b",
    pending: "#ffd166",
  };

  const statusColors = {
    Оплачено: colors.paid,
    "Оплаты нет": colors.unpaid,
    "Ожидает подтверждения": colors.pending,
    Неизвестно: colors.warning,
  };

  // Загрузка данных
  useEffect(() => {
    document.title = t("accounts.title");
    loadDashboardData();
  }, [t]);

  const loadDashboardData = async () => {
    try {
      const response = await GetData({
        module: "finance",
        block: "accounts",
      });
      console.log("response", response);
      setData(response);
    } catch (err) {
      setError(t("accounts.loadFailed"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Расчеты на фронтенде
  const dashboardData = useMemo(() => {
    if (!data) return null;

    const { period, transactions, clinics, config, meta } = data;

    // Фильтрация транзакций
    let filteredTransactions = [...transactions.list];

    // Фильтр по статусу
    if (filterStatus === "paid") {
      filteredTransactions = filteredTransactions.filter(
        (t) => t.status.id === "10",
      );
    } else if (filterStatus === "unpaid") {
      filteredTransactions = filteredTransactions.filter(
        (t) => t.status.id !== "10",
      );
    }

    // Фильтр по методу оплаты
    if (filterMethod !== "all") {
      filteredTransactions = filteredTransactions.filter(
        (t) => t.payment_method.id === filterMethod,
      );
    }

    // Фильтр по клинике
    if (selectedClinic !== "all") {
      filteredTransactions = filteredTransactions.filter(
        (t) => t.clinic_id === selectedClinic,
      );
    }

    if (debouncedSearchTerm.trim()) {
      const query = debouncedSearchTerm.trim().toLowerCase();
      filteredTransactions = filteredTransactions.filter((t) =>
        String(t.patient_name || t.patient?.name || t.fio || "")
          .toLowerCase()
          .includes(query),
      );
    }

    // 1. Общая статистика
    const totalStats = {
      totalAmount: filteredTransactions.reduce((sum, t) => sum + t.amount, 0),
      totalCount: filteredTransactions.length,
      paidAmount: filteredTransactions
        .filter((t) => t.status.id === "10")
        .reduce((sum, t) => sum + t.amount, 0),
      paidCount: filteredTransactions.filter((t) => t.status.id === "10")
        .length,
      unpaidAmount: filteredTransactions
        .filter((t) => t.status.id !== "10")
        .reduce((sum, t) => sum + t.amount, 0),
      unpaidCount: filteredTransactions.filter((t) => t.status.id !== "10")
        .length,
      avgTransaction:
        filteredTransactions.length > 0
          ? filteredTransactions.reduce((sum, t) => sum + t.amount, 0) /
            filteredTransactions.length
          : 0,
    };

    // 2. Статистика по статусам
    const statusStats = {};
    filteredTransactions.forEach((transaction) => {
      const statusId = transaction.status.id;
      const statusName =
        config.statuses[statusId] || transaction.status.name || t("statuses.unknown");

      if (!statusStats[statusId]) {
        statusStats[statusId] = {
          id: statusId,
          name: statusName,
          count: 0,
          amount: 0,
        };
      }
      statusStats[statusId].count++;
      statusStats[statusId].amount += transaction.amount;
    });

    const statusChartData = Object.values(statusStats)
      .map((stat) => ({
        name: stat.name,
        value: stat.amount,
        count: stat.count,
      }))
      .sort((a, b) => b.value - a.value);

    // 3. Статистика по методам оплаты
    const methodStats = {};
    filteredTransactions.forEach((transaction) => {
      const methodId = transaction.payment_method.id;
      const methodName =
        config.payment_methods[methodId] ||
        transaction.payment_method.name ||
        t("statuses.unknown");

      if (!methodStats[methodId]) {
        methodStats[methodId] = {
          id: methodId,
          name: methodName,
          count: 0,
          amount: 0,
        };
      }
      methodStats[methodId].count++;
      methodStats[methodId].amount += transaction.amount;
    });

    const methodChartData = Object.values(methodStats)
      .map((stat) => ({
        name: stat.name,
        value: stat.amount,
        count: stat.count,
      }))
      .sort((a, b) => b.value - a.value);

    // 4. Ежедневная статистика
    const dailyData = {};
    filteredTransactions.forEach((transaction) => {
      const date = transaction.created_at.split(" ")[0];

      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          income: 0,
          count: 0,
          paid: 0,
          unpaid: 0,
        };
      }

      dailyData[date].income += transaction.amount;
      dailyData[date].count++;

      if (transaction.status.id === "10") {
        dailyData[date].paid += transaction.amount;
      } else {
        dailyData[date].unpaid += transaction.amount;
      }
    });

    const dailyChartData = Object.values(dailyData)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((day) => ({
        ...day,
        date: formatDate(day.date),
      }));

    // 5. Статистика по клиникам
    const clinicChartData = clinics.list
      .map((clinic) => {
        const clinicTransactions = filteredTransactions.filter(
          (t) => t.clinic_id === clinic.clinic_id,
        );
        const paidAmount = clinicTransactions
          .filter((t) => t.status.id === "10")
          .reduce((sum, t) => sum + t.amount, 0);

        return {
          id: clinic.clinic_id,
          name: clinic.name,
          balance: parseFloat(clinic.balance) || 0,
          total: clinic.stats?.total || 0,
          paid: paidAmount,
          unpaid: (clinic.stats?.total || 0) - paidAmount,
          count: clinicTransactions.length,
          avgTransaction:
            clinicTransactions.length > 0
              ? clinicTransactions.reduce((sum, t) => sum + t.amount, 0) /
                clinicTransactions.length
              : 0,
        };
      })
      .filter(
        (clinic) =>
          clinic.count > 0 ||
          selectedClinic === "all" ||
          clinic.id === selectedClinic,
      );

    // 6. Топ транзакций
    const topTransactions = [...filteredTransactions]
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10)
      .map((t) => ({
        id: t.id,
        clinic:
          clinics.list.find((c) => c.clinic_id === t.clinic_id)?.name ||
          t.clinic_name,
        amount: t.amount,
        status: config.statuses[t.status.id] || t.status.name,
        method:
          config.payment_methods[t.payment_method.id] || t.payment_method.name,
        date: formatDateTime(t.created_at),
        isPaid: t.status.id === "10",
      }));

    return {
      period,
      totalStats,
      statusChartData,
      methodChartData,
      dailyChartData,
      clinicChartData,
      topTransactions,
      filteredTransactions,
      clinics: clinics.list,
      config,
      meta,
    };
  }, [data, selectedClinic, filterStatus, filterMethod, debouncedSearchTerm, locale, t]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return (
      <div className="finance-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t("accounts.loadingData")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="finance-dashboard">
        <div className="error-message">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={loadDashboardData}>{t("actions.retry", { ns: "common" })}</button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="finance-dashboard">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p>{t("accounts.noData")}</p>
        </div>
      </div>
    );
  }

  const {
    period,
    totalStats,
    statusChartData,
    methodChartData,
    dailyChartData,
    clinicChartData,
    topTransactions,
    clinics,
    config,
  } = dashboardData;

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <div className="finance-dashboard">
            {/* Заголовок и фильтры */}
            <BlockTitle
              title={
                <TitleWithIcon
                  icon={historyTransaction}
                  title={t("accounts.title")}
                  showStatus={false}
                  status={""}
                />
              }
              buttons={[]}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              {/* Панель поиска */}
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""}`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">
                        {t("filters.title", { ns: "common" })}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="button-default light"
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                    >
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </button>
                  </div>
                  <div className="search-section">
                    <label>{t("filters.searchLabel", { ns: "common" })}</label>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("accounts.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

    </Container>
  );
}

export default Accounts;
